import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Flashcard, UserProgress, StudyTechnique } from '../../types';
import { SpacedRepetition } from '../../utils/spacedRepetition';
import { Flashcard as FlipCard } from '../Flashcard';

const StudyContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  margin: '0 auto',
  padding: '16px',
  background: 'var(--surface)',
  border: '1px solid var(--card-border)',
  borderRadius: '24px',
  boxShadow: '0 20px 50px rgba(15, 23, 42, 0.12)',
  backdropFilter: 'blur(18px)',
  minHeight: 'calc(100vh - 140px)',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  [theme.breakpoints.down('sm')]: {
    padding: '10px',
    minHeight: 'calc(100vh - 120px)',
  },
}));

interface ActiveRecallProps {
  flashcards: Flashcard[];
  userProgress: UserProgress[];
  onProgressUpdate: (progress: UserProgress) => void;
  onSessionComplete: (sessionData: any) => void;
}

export const ActiveRecall: React.FC<ActiveRecallProps> = ({
  flashcards,
  userProgress,
  onProgressUpdate,
  onSessionComplete
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [startTime] = useState(Date.now());
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [sessionComplete, setSessionComplete] = useState(false);

  const currentCard = flashcards[currentIndex];
  const progress = flashcards.length > 0 ? ((currentIndex + 1) / flashcards.length) * 100 : 0;

  // Get user progress for current card
  const currentProgress = userProgress.find(p => p.flashcardId === currentCard?.id);

  useEffect(() => {
    if (sessionComplete) {
      const sessionData = {
        technique: StudyTechnique.ACTIVE_RECALL,
        flashcardsStudied: flashcards.length,
        correctAnswers,
        totalTime: Math.floor((Date.now() - startTime) / 60000), // minutes
        accuracy: (correctAnswers / flashcards.length) * 100
      };
      onSessionComplete(sessionData);
    }
  }, [sessionComplete, correctAnswers, flashcards.length, startTime, onSessionComplete]);

  const handleAnswer = (quality: number) => {
    if (!currentCard) return;

    if (currentProgress) {
      const updatedProgress = SpacedRepetition.calculateNextReview(currentProgress, quality);
      onProgressUpdate(updatedProgress);
    }

    if (quality >= 3) {
      setCorrectAnswers(prev => prev + 1);
    }

    if (currentIndex < flashcards.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setSessionComplete(true);
    }
  };

  if (sessionComplete) {
    return (
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <StudyContainer>
          <Dialog open={true} maxWidth="sm" fullWidth>
            <DialogTitle>Session Complete!</DialogTitle>
            <DialogContent>
              <Typography variant="h6" gutterBottom>
                Active Recall Session Summary
              </Typography>
              <Typography>Flashcards studied: {flashcards.length}</Typography>
              <Typography>Correct answers: {correctAnswers}</Typography>
              <Typography>Accuracy: {((correctAnswers / flashcards.length) * 100).toFixed(1)}%</Typography>
              <Typography>Time spent: {Math.floor((Date.now() - startTime) / 60000)} minutes</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => window.location.reload()}>Start New Session</Button>
            </DialogActions>
          </Dialog>
        </StudyContainer>
      </motion.div>
    );
  }

  if (!currentCard) {
    return (
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <StudyContainer>
          <Typography>No flashcards available for study.</Typography>
        </StudyContainer>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
      <StudyContainer>
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Active Recall
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Card {currentIndex + 1} of {flashcards.length}
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progress} />
        </Box>

        <Box sx={{ flexGrow: 1, display: 'flex' }}>
          <FlipCard
            flashcard={currentCard}
            studyMode
            onAnswer={handleAnswer}
          />
        </Box>
      </StudyContainer>
    </motion.div>
  );
};
